import { useRef, useState } from 'react';
import { FaImage, FaTimes } from 'react-icons/fa';

/**
 * Baris upload asset — preview, info, Pilih file + Simpan (selaras dengan .input).
 */
const AssetUploadRow = ({
    title,
    hint,
    currentUrl,
    file,
    onFileChange,
    onUpload,
    uploading = false,
    disabled = false,
    accept = 'image/png,image/jpeg,image/jpg',
    validateFile,
    previewBoxClassName = 'h-12 w-24',
    imageClassName = 'max-h-full max-w-full object-contain',
}) => {
    const inputRef = useRef(null);
    const [dragging, setDragging] = useState(false);

    const isAcceptedFile = validateFile || ((next) => /\.(jpe?g|png)$/i.test(next.name));

    const pickFile = (next) => {
        if (!next) {
            onFileChange(null);
            return;
        }
        if (!isAcceptedFile(next)) return;
        onFileChange(next);
    };

    const openPicker = () => {
        if (!disabled && !uploading) inputRef.current?.click();
    };

    const onDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragging(false);
        if (disabled || uploading) return;
        const dropped = e.dataTransfer?.files?.[0];
        if (dropped) pickFile(dropped);
    };

    const localPreview = file ? URL.createObjectURL(file) : null;
    const previewSrc = localPreview || currentUrl;
    const statusText = file
        ? `${file.name} · ${(file.size / 1024).toFixed(0)} KB`
        : hint;

    return (
        <div
            className={[
                'rounded-xs border bg-canvas transition-[border-color] duration-tesla dark:bg-tile-2',
                dragging
                    ? 'border-primary dark:border-primary-on-dark'
                    : 'border-border-strong dark:border-white/10',
                disabled ? 'opacity-60' : '',
            ].join(' ')}
            onDragEnter={(e) => {
                e.preventDefault();
                if (!disabled && !uploading) setDragging(true);
            }}
            onDragOver={(e) => {
                e.preventDefault();
                if (!disabled && !uploading) setDragging(true);
            }}
            onDragLeave={(e) => {
                e.preventDefault();
                setDragging(false);
            }}
            onDrop={onDrop}
        >
            <div className="flex flex-col gap-3 p-3 sm:flex-row sm:items-center">
                <div
                    className={`flex shrink-0 items-center justify-center overflow-hidden rounded-xs border border-hairline bg-surface-soft dark:border-white/10 dark:bg-white/[0.04] ${previewBoxClassName}`}
                >
                    {previewSrc ? (
                        <img src={previewSrc} alt={title} className={imageClassName} />
                    ) : (
                        <FaImage className="text-lg text-muted/40" />
                    )}
                </div>

                <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-ink dark:text-white">{title}</p>
                    <p className="mt-0.5 truncate text-xs text-muted">{statusText}</p>
                </div>

                <div className="flex shrink-0 items-center gap-2 sm:ml-auto">
                    {file ? (
                        <button
                            type="button"
                            disabled={disabled || uploading}
                            onClick={() => {
                                pickFile(null);
                                if (inputRef.current) inputRef.current.value = '';
                            }}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-xs text-muted hover:bg-surface-soft hover:text-ink dark:hover:bg-white/10 dark:hover:text-white"
                            aria-label="Batalkan pilihan"
                        >
                            <FaTimes className="text-xs" />
                        </button>
                    ) : null}
                    <button
                        type="button"
                        disabled={disabled || uploading}
                        onClick={openPicker}
                        className="btn-secondary min-h-9 px-4 text-sm"
                    >
                        Pilih file
                    </button>
                    <button
                        type="button"
                        disabled={disabled || uploading || !file}
                        onClick={onUpload}
                        className="btn-primary min-h-9 px-4 text-sm disabled:opacity-50"
                    >
                        {uploading ? 'Menyimpan...' : 'Simpan'}
                    </button>
                </div>
            </div>

            <input
                ref={inputRef}
                type="file"
                accept={accept}
                className="hidden"
                disabled={disabled || uploading}
                onChange={(e) => pickFile(e.target.files?.[0] || null)}
            />
        </div>
    );
};

export default AssetUploadRow;
